// apps/client/src/ui-kit/WelcomeModal.tsx
//
// Welcome bonus modal — +1000 BingoChips for new users.
// Golden Path 1: CTA says "PLAY MY FIRST GAME!" and auto-navigates to lobby.
//

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../store/userStore';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';
import Icon from './Icon';
import { useAudio } from '../providers/AudioProvider';

interface WelcomeModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Called to navigate user straight to the game lobby */
    onPlayFirstGame?: () => void;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose, onPlayFirstGame }) => {
    const { t } = useTranslation();
    const claimWelcomeBonus = useUserStore((s) => s.claimWelcomeBonus);
    const welcomeBonusAmount = useUserStore((s) => s.welcomeBonusAmount);

    // Animated counter: 0 → dynamic bonus
    const animatedReward = useAnimatedCounter(isOpen ? welcomeBonusAmount : 0, { duration: 1500 });
    const { playClaim } = useAudio();

    const handleClaim = () => {
        claimWelcomeBonus();
        playClaim(); // Call playClaim() here
        if (onPlayFirstGame) {
            onPlayFirstGame();
        } else {
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ scale: 0.5, opacity: 0, y: 30 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', bounce: 0.3 }}
                        className="relative w-full max-w-sm bg-white rounded-3xl p-8 overflow-hidden shadow-[0_0_60px_rgba(251,191,36,0.3)]"
                    >
                        {/* Background decoration */}
                        <div className="absolute -top-10 -right-10 text-[150px] opacity-5 rotate-12 select-none">🎁</div>

                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-[#1A0B2E]/30 hover:text-[#1A0B2E]/60 z-10"
                        >
                            <Icon name="close" />
                        </button>

                        <div className="relative z-10 text-center">
                            {/* Emoji */}
                            <motion.div
                                animate={{ scale: [1, 1.15, 1], rotate: [0, -5, 5, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="text-7xl mb-4"
                            >
                                🎁
                            </motion.div>

                            <h2 className="text-2xl font-black text-[#1A0B2E] mb-2 font-display">
                                {t('welcome.title')}
                            </h2>
                            <p className="text-[#1A0B2E]/50 text-sm mb-6">
                                {t('welcome.subtitle')}
                            </p>

                            {/* Reward amount — animated odometer */}
                            <div className="bg-gold-gradient rounded-3xl py-5 px-6 mb-6 shadow-neon-gold">
                                <div className="text-5xl font-black text-[#1a0b2e] tracking-tight">
                                    +{animatedReward.toLocaleString()}
                                </div>
                                <div className="text-[#1a0b2e]/70 font-bold text-sm uppercase tracking-wider">
                                    🌟 BingoChips
                                </div>
                            </div>

                            {/* CTA — "PLAY MY FIRST GAME!" per Golden Path 1 */}
                            <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={handleClaim}
                                className="w-full py-4 rounded-pill bg-gradient-to-b from-[#6D28D9] to-[#4C1D95] text-white font-black text-lg uppercase tracking-wider shadow-glow-purple hover:from-[#7C3AED] transition-all"
                            >
                                {t('welcome.playFirst')}
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
